import axios from 'axios';
import { getSupplierIdByNameSync, getSupplierNameByIdSync } from '../data/suppliers';

// Get API base URL from environment variable
const getApiBaseUrl = () => {
    return process.env.REACT_APP_API_URL;
};

// API credentials for backend authentication
const getCredentials = () => {
   // Get default credentials from environment variables
  const defaultUsername = process.env.REACT_APP_EXCEL_USERNAME || 'qara-sheets';
  const defaultPassword = process.env.REACT_APP_EXCEL_PASSWORD || 'qara2024';
  
  // Try to get dynamic credentials first, fall back to environment-specific ones
  const username = localStorage.getItem('api_username') || defaultUsername;
  const password = localStorage.getItem('api_password') || defaultPassword;
  const supplierName = localStorage.getItem('excel_supplier_name') || 'Saint-Gobain';
  // Use tenant_id from new auth flow (tanantId from URL params) if available,
  // otherwise fall back to name-based lookup for backward compatibility
  const supplierId =
    localStorage.getItem('tenant_id') ||
    getSupplierIdByNameSync(supplierName) ||
    '6683aa63d71fa531dd60b807';
  
  return { username, password, supplierName, supplierId };
};

const apiClient = axios.create({
  timeout: 120000, // 2 minutes timeout for debugging
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json, */*',
  }
});
// Request interceptor to add auth headers and set dynamic base URL
apiClient.interceptors.request.use(
  (config) => {
    // Set dynamic base URL based on selected environment
    config.baseURL = getApiBaseUrl();
    
    const { username, password, supplierId } = getCredentials();
    
    // Add fixed API credentials to all requests - matching Google Apps Script format
    config.headers['Username'] = username || 'qara-sheets';
    config.headers['password'] = password || 'qara2024';
    
    // Add X-API-Key header for new API endpoints
    config.headers['X-API-Key'] = process.env.REACT_APP_X_API_KEY || 'dw_web_api_k3y_qwerty123456789abcdef0123456789abcdef';

    // Add supplier parameter to requests based on endpoint type
    // Skip adding supplier param for endpoints that already have it in the URL path or params
    const isUserTypesEndpoint = config.url?.includes('/user-types/findUserTypesBySupplier/');
    const isResetLogsEndpoint = config.url?.includes('/reset-logs');
    const isJoiningRequestsEndpoint = config.url?.includes('/joining-request/data/sheet/joining-request');
    const isWalletEndpoint = config.url?.includes('/wallet-transactions/') && 
                            (config.url.includes('/WalletTransactionSheet') || 
                             config.url.includes('/deliverCash') || 
                             config.url.includes('/deliverTransfer'));
    const isMultyOperationsEndpoint = config.url?.includes('/wallet-transactions/multy-operations');
    
    if (!config.params?.supplierId && !config.params?.supplier && !isUserTypesEndpoint && !isResetLogsEndpoint && !isJoiningRequestsEndpoint) {
      if (isWalletEndpoint) {
        // Use supplier name for wallet endpoints (except multy-operations and WelcomeBonus)
        const isWelcomeBonusEndpoint = config.url?.includes('/WelcomeBonusForSheet');
        if (isWelcomeBonusEndpoint) {
          // Use supplier ID for Welcome Bonus endpoint
          config.params = { ...config.params, supplierId: supplierId };
        } else {
          const { supplierName } = getCredentials();
          config.params = { ...config.params, supplier: supplierName };
        }
      } else if (isMultyOperationsEndpoint) {
        // Use supplier ID for multy-operations endpoint
        console.log('Multy operations endpoint detected, using supplier ID:', supplierId);
        config.params = { ...config.params, supplier: supplierId };
      } else if (supplierId) {
        // Use supplier ID for other endpoints
        config.params = { ...config.params, supplier: supplierId };
      }
    }
    
    // Ensure Accept header is always set
    if (!config.headers['Accept']) {
      config.headers['Accept'] = 'application/json, */*';
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor for error handling
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    const environment = localStorage.getItem('selectedEnvironment') || 'datawarehouse';
    
    // Only redirect to login for actual authentication failures on critical endpoints
    // Skip auto-logout for API data endpoints to allow graceful error handling
    if ((error.response?.status === 401 || error.response?.status === 403) && error.config) {
      const url = error.config.url || '';
      
      // List of endpoints that should trigger immediate logout due to auth failure
      const criticalAuthEndpoints = ['/health', '/auth/check-iam-access'];
      const isCriticalEndpoint = criticalAuthEndpoints.some(endpoint => url.includes(endpoint));
      
      // Skip auto-redirect for non-production environments on data endpoints
      // Let the calling code handle the error gracefully with fallbacks
      if (isCriticalEndpoint && environment !== 'production') {
        localStorage.removeItem('excel_supplier_name');
        window.location.href = '/login';
      }
      
      // For other endpoints, especially user-types and data endpoints,
      // let the calling code handle errors and provide fallbacks
      // Log the issue for debugging
      if (!isCriticalEndpoint) {
        console.warn(`API endpoint ${url} returned ${error.response.status}. Allowing caller to handle error with fallback.`);
      }
    }
    
    return Promise.reject(error);
  }
);

// Simple cache for user types to prevent multiple rapid API calls
const userTypesCache = new Map<string, { data: any; timestamp: number }>();
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

// Request deduplication to prevent multiple simultaneous calls
const pendingRequests = new Map<string, Promise<any>>();

export const apiService = {
  // Helper function for Excel endpoints
  callExcelEndpoint: (endpoint: string, params: any = {}) => {
    return apiClient.get(endpoint, { 
      params,
      headers: {
        'Accept': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/json, */*'
      }
    });
  },

  // Google Sheets APIs - Direct endpoints from main API
  getUsersExcel: (params: any = {}) => 
    apiService.callExcelEndpoint('/users/get-all-users-excel', params),
  getFeeds: (params: any = {}) => 
    apiService.callExcelEndpoint('/users/production/feeds', params),
  getUsersWithAcceptedBy: (params: any = {}) =>
    apiClient.get('/users/usersWithAcceptedby', { params }),
  getUsersData: (params: any = {}) =>
    apiClient.get('/UsersData', { params }),
  getReferralSheet: (params: any = {}) =>
    apiClient.get('/users/data/sheet/referal', { params }),
  getReferredUsersSheet: (params: any = {}) =>
    apiClient.get('/users/data/sheet/referredUser', { params }),
  getEndOfMonthSheet: (params: any = {}) =>
    apiClient.get('/users/data/sheet/endOfMonth', { params }),

  // Get user types for current supplier
  getUserTypes: async () => {
    const { supplierId } = getCredentials();
    
    // Check cache first
    const cacheKey = `userTypes_${supplierId}`;
    const cachedData = userTypesCache.get(cacheKey);
    if (cachedData && (Date.now() - cachedData.timestamp) < CACHE_DURATION) {
      console.log('Returning cached user types for supplier:', supplierId);
      return cachedData.data;
    }
    
    // Check if there's already a pending request for this supplier
    const requestKey = `getUserTypes_${supplierId}`;
    if (pendingRequests.has(requestKey)) {
      console.log('Joining existing request for user types:', supplierId);
      return await pendingRequests.get(requestKey);
    }
    
    // Create new request and store it in pending requests
    const requestPromise = (async () => {
      try {
        // Call the new API endpoint to get user types for the current supplier
        console.log('Fetching user types for supplier:', supplierId, 'Credentials:', {
          username: getCredentials().username ? '[SET]' : '[MISSING]',
          password: getCredentials().password ? '[SET]' : '[MISSING]',
          supplierId: supplierId
        });
        
        const response = await apiClient.get(`/user-types/findUserTypesBySupplier/${supplierId}`);
        
        if (response.data && response.data.status === 201 && response.data.body && response.data.body.length > 0) {
          const supplierData = response.data.body[0]; // Get the first (and should be only) supplier data
          
          if (supplierData.userTypes && Array.isArray(supplierData.userTypes)) {
            // Extract user type names from the API response
            const userTypes = supplierData.userTypes
              .map((userType: any) => userType.name)
              .filter((name: string) => name && name.trim() !== '') // Remove empty names
              .sort(); // Sort alphabetically
            
            // Remove duplicates
            const uniqueUserTypes = Array.from(new Set(userTypes));
            
            const result = {
              data: uniqueUserTypes.length > 0 ? uniqueUserTypes : ['Admin', 'User'],
              totalUserTypes: supplierData.totalUserTypes || uniqueUserTypes.length
            };
            
            console.log('Successfully fetched user types:', uniqueUserTypes);
            
            // Cache the result
            userTypesCache.set(cacheKey, { data: result, timestamp: Date.now() });
            
            return result;
          }
        }
        
        // If no valid response, log and return default
        console.warn('Invalid user types response format from API:', response.data);
        
        const defaultResult = {
          data: ['Admin', 'User']
        };
        
        // Cache default result for a shorter time
        userTypesCache.set(cacheKey, { data: defaultResult, timestamp: Date.now() });
        
        return defaultResult;
      } catch (error: any) {
        console.error('Error loading user types from API:', error?.message || error, {
          endpoint: `/user-types/findUserTypesBySupplier/${supplierId}`,
          status: error?.response?.status,
          statusText: error?.response?.statusText,
          data: error?.response?.data
        });
        
        // Fallback to default user types - don't fail the app
        const defaultResult = {
          data: ['Admin', 'User']
        };
        
        // Cache error result for a shorter time  
        userTypesCache.set(cacheKey, { data: defaultResult, timestamp: Date.now() });
        
        return defaultResult;
      } finally {
        // Remove from pending requests when done
        pendingRequests.delete(requestKey);
      }
    })();
    
    // Store the promise in pending requests
    pendingRequests.set(requestKey, requestPromise);
    
    return await requestPromise;
  },

  // QR Code APIs
  getUserScanAnalysis: (params: any = {}) =>
    apiClient.get('/qr-code/userScanAnalisis', { params }),
  getScansDetailsPerUserType: (params: any = {}) =>
    apiClient.get('/qr-code/scansDetailsPerUserType', { params }),
  getWarehouseScanInOutDetails: (params: any = {}) =>
    apiClient.get('/qr-code/warehouseScanInOutDetails', { params }),
  getDeactivatedIncentiveQR: (params: any = {}) =>
    apiService.callExcelEndpoint('/qr-code/deactivatedIncentiveQR', params),
  getConfigurationSheetCounterPerProduct: (params: any = {}) =>
    apiClient.get('/qr-code/configurationSheetCounterPerProduct', { params }),
  getWarehouseQuantityAggregation: (params: any = {}) =>
    apiClient.get('/qr-code/warehouseQuantityAggregation', { params }),
  getOrderScanoutFromWarehouse: (params: any = {}) => 
    apiClient.get('/qr-code/orderScanoutFromWarehouse', { params }),
  getScanPerProductDetails: (params: any = {}) =>
    apiClient.get('/qr-code/qrScanPerProductDetails', { params }),

  // Wallet APIs - additional methods
  getDeliverCash: (params: any = {}) =>
    apiClient.get('/wallet-transactions/deliverCash', { params }),
  getDeliverTransfer: (params: any = {}) =>
    apiClient.get('/wallet-transactions/deliverTransfer', { params }),
  getWelcomeBonus: (params: any = {}) => {
    // Add supplierId parameter from localStorage if not provided
    const { supplierId } = getCredentials();
    const defaultParams = { 
      supplierId: params.supplierId || supplierId || '',
      ...params 
    };
    return apiClient.get('/wallet-transactions/WelcomeBonusForSheet', { params: defaultParams });
  },
  getWalletTransactionSheet: (params: any = {}) =>
    apiClient.get('/wallet-transactions/WalletTransactionSheet', { params }),
  
  // Other APIs
  getInvoicePhotosSheet: (params: any = {}) => {
    const { supplierId } = getCredentials();
    return apiClient.get(`/invoice-photos/getInvoicesPhotosForSheet/${supplierId}`, { params: {} });
  },
  getPaymentLogs: (params: any = {}) =>
    apiClient.get('/payment-logs', { params }),
  getResetLogs: (params: any = {}) => {
    // Special handling for reset-logs endpoint - uses supplierName instead of supplier
    const { supplierName } = getCredentials();
    const resetLogsParams = {
      supplierName: supplierName,
      ...params
    };
    return apiClient.get('/reset-logs', { params: resetLogsParams });
  },
  getJoiningRequests: (params: any = {}) => {
    const { supplierId } = getCredentials();
    const requestParams = { ...params, supplierId };
    return apiClient.get('/joining-request/data/sheet/joining-request', { params: requestParams });
  },
  getReturnSystemSheet: (params: any = {}) => {
    const { supplierId } = getCredentials();
    const returnSystemParams = { ...params, supplierId };
    return apiClient.get('/return-system', { params: returnSystemParams });
  },
  getReturnSystemScanView: (params: any = {}) => {
    const { supplierId } = getCredentials();
    const returnSystemParams = { ...params, supplierId };
    return apiClient.get('/return-system/scan-view', { params: returnSystemParams });
  },
  getDepositAmountOperations: (params: any = {}) =>
    apiClient.get('/wallet-transactions/multy-operations', { params }),
  submitDepositAmount: (params: any) =>
    apiClient.post('/wallet-transactions/multy-operations', params),
  getGiftsSheet: (params: any = {}) => {
    const { supplierName } = getCredentials();
    const giftsParams = {
      supplier: supplierName,
      ...params
    };
    return apiClient.get('/client-request/data/gift/sheet', { params: giftsParams });
  },

  // Get available endpoints for dashboard
  getEndpoints: async () => {
    // Mock response for now since this endpoint might not exist in main API
    return {
      data: {
        users: [
          { endpoint: 'Users', category: 'Users', originalPath: '/users/usersWithAcceptedby' },
          { endpoint: 'Users Data',displayName: 'Master Data' ,category: 'Users', originalPath: '/UsersData' },
          { endpoint: 'Users Types', category: 'Users', originalPath: '/users/get-all-users-excel' },
          { endpoint: 'Users Points', category: 'Users', originalPath: '/users/production/feeds' },
          // { endpoint: 'users/qara-feeds', category: 'Users', originalPath: '/users/qara/feeds' },
          // { endpoint: 'users/notification-feeds', category: 'Users', originalPath: '/users/notification/feeds' },
          // { endpoint: 'users/kz-report', category: 'Users', originalPath: '/users/kz/users-report' },
          { endpoint: 'Referral Users',displayName: 'Referral' ,category: 'Users', originalPath: '/users/data/sheet/referal' },
          { endpoint: 'Referred Users', category: 'Users', originalPath: '/users/data/sheet/referredUser' },
          { endpoint: 'End Of Month', category: 'Incentive', originalPath: '/users/data/sheet/endOfMonth' }
        ],
        'qr codes': [
          { endpoint: 'User Details Per Product Per Action',displayName: 'Scans' ,category: 'QR Codes', originalPath: '/qr-code/userScanAnalisis' },
          { endpoint: 'Scans per product per user type', category: 'QR Codes', originalPath: '/qr-code/scansDetailsPerUserType' },
          { endpoint: 'Warehouse Scan In/Out',displayName: 'Orders from Warehouse' ,category: 'QR Codes', originalPath: '/qr-code/warehouseScanInOutDetails' },
          { endpoint: 'Deactivated Incentive QR', category: 'QR Codes', originalPath: '/qr-code/deactivatedIncentiveQR' },
          { endpoint: 'Configuration Result Per Product', category: 'QR Codes', originalPath: '/qr-code/configurationSheetCounterPerProduct' },
          { endpoint: 'Order Scanout From Warehouse', category: 'QR Codes', originalPath: '/qr-code/orderScanoutFromWarehouse' },
          { endpoint: 'Warehouse Quantity Aggregation', category: 'Wallet', originalPath: '/qr-code/warehouseQuantityAggregation' },
          { endpoint: 'Scan Per Product details', category: 'QR Codes', originalPath: '/qr-code/qrScanPerProductDetails' },
        ],
        wallet: [
          { endpoint: 'Wallet Transactions', category: 'Wallet', originalPath: '/wallet-transactions/WalletTransactionSheet' },
          { endpoint: 'Deliver Cash', category: 'Wallet', originalPath: '/wallet-transactions/deliverCash' },
          { endpoint: 'Deliver Transfer', category: 'Wallet', originalPath: '/wallet-transactions/deliverTransfer' },
          { endpoint: 'Welcome Bonus', category: 'Wallet', originalPath: '/wallet-transactions/WelcomeBonusForSheet' },
        ],
        Paymob: [
          { endpoint: 'Paymob', displayName: 'Smart Wallet Transactions', category: 'Wallet', originalPath: '/payment-logs' },
        ],
        Invoice: [
          { endpoint: 'invoice-photos', category: 'Invoice', originalPath: '/invoice-photos/getInvoicesPhotosForSheet' },
        ],
        'Transaction Logs': [
          { endpoint: 'reset-logs', category: 'Transaction Logs', originalPath: '/reset-logs' },
        ],
        'Joining Requests': [
          { endpoint: 'joining-requests', category: 'Joining Requests', originalPath: '/joining-request/data/sheet/joining-request' },
        ],
        'return system': [
          { endpoint: 'Return System',displayName: 'Returns' ,category: 'Return System', originalPath: '/return-system/scan-view' },
          { endpoint: 'Incentive', category: 'Return System', originalPath: '/return-system/returnSystemSheet' },

        ],
        'Deposit Amount': [
          { endpoint: 'Deposit Amount', category: 'Deposit Amount', originalPath: '/wallet-transactions/multy-operations' },
          { endpoint: 'Deposit Amount Log', category: 'Deposit Amount', originalPath: '/wallet-transactions/multy-operations' }
        ],
        'Gifts': [
          { endpoint: 'Gifts', category: 'Gifts', originalPath: '/client-request/data/gift/sheet' },
        ]

      }
    };
  },

  // Dynamic API call for any endpoint
  callDynamicAPI: (endpoint: string, params: any) => {
    // Extract the path from the endpoint
    const path = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
    
    // Check if this is an Excel endpoint
    if (path.includes('/excel')) {
      return apiService.callExcelEndpoint(path, params);
    }
    
    return apiClient.get(path, { params });
  }
};
